import * as Sentry from '@sentry/vue'
import OpenAIConstants from '@/constant/openAI'
import i18nPresenter from '@/i18nPresenter'
import utils from '@/util'

const ADD_MESSAGE_TO_CONVERSATION = 'ADD_MESSAGE_TO_CONVERSATION'
const SET_CONVERSATION = 'SET_CONVERSATION'
const SET_NUMBER_OF_SLIDES_ADDED = 'SET_NUMBER_OF_SLIDES_ADDED'
const SET_MESSAGE_ADDED_FROM = 'SET_MESSAGE_ADDED_FROM'
const PENDING_MESSAGE_DELAY_TIME = 5000

// HELPERS
const addSuccessMessageToConversation = async (hasConversation, lastMessage, dispatch, rootState) => {
  try {
    // Check if the last message is a success message
    const lastMessageIsSuccessMessage = hasConversation &&
      lastMessage.type === OpenAIConstants.AI_SYSTEM_SUCCESS_MESSAGE_TYPE
    if (lastMessageIsSuccessMessage) return // No need to add another success message

    await dispatch('createNewMessageAction', {
      presentationId: rootState.presentation.id,
      role: OpenAIConstants.AI_ROLE_SYSTEM,
      content: i18nPresenter.t('Generated successfully!'),
      type: OpenAIConstants.AI_SYSTEM_SUCCESS_MESSAGE_TYPE,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const addErrorMessageToConversation = async (hasConversation, lastMessage, dispatch, rootState) => {
  try {
    // Check if the last message is an error message
    const lastMessageIsErrorMessage = hasConversation &&
      lastMessage.type === OpenAIConstants.AI_SYSTEM_ERROR_MESSAGE_TYPE
    if (lastMessageIsErrorMessage) return // No need to add another error message

    await dispatch('createNewMessageAction', {
      presentationId: rootState.presentation.id,
      role: OpenAIConstants.AI_ROLE_SYSTEM,
      content: i18nPresenter.t("I'm unable to complete your request right now. Would you mind trying again? 🥺"),
      type: OpenAIConstants.AI_SYSTEM_ERROR_MESSAGE_TYPE,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const addGenerationStoppedMessageToConversation = async (hasConversation, lastMessage, dispatch, rootState) => {
  try {
    // Check if the last message is "generation stopped" message
    const lastMessageIsStopMessage = hasConversation &&
     lastMessage.type === OpenAIConstants.AI_SYSTEM_GENERATION_STOPPED_MESSAGE_TYPE
    if (lastMessageIsStopMessage) return // No need to add another stop message

    await dispatch('createNewMessageAction', {
      presentationId: rootState.presentation.id,
      role: OpenAIConstants.AI_ROLE_SYSTEM,
      content: i18nPresenter.t('Generation stopped.'),
      type: OpenAIConstants.AI_SYSTEM_GENERATION_STOPPED_MESSAGE_TYPE,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const addNumberOfSlidesAddedMessageToConversation = async (hasConversation,
  lastMessage, numberOfSlidesAdded, dispatch, rootState) => {
  try {
    // Check if the last message is a added-slide message
    const lastMessageIsAddedSlideMessage = hasConversation &&
     lastMessage.type === OpenAIConstants.AI_SYSTEM_ADDED_SLIDE_MESSAGE_TYPE
    if (lastMessageIsAddedSlideMessage) return
    const content = numberOfSlidesAdded > 1
      ? `${numberOfSlidesAdded} ${i18nPresenter.t('slides have been added to your presentation!')}`
      : i18nPresenter.t('1 slide has been added to your presentation!')

    await dispatch('createNewMessageAction', {
      presentationId: rootState.presentation.id,
      role: OpenAIConstants.AI_ROLE_SYSTEM,
      content,
      type: OpenAIConstants.AI_SYSTEM_ADDED_SLIDE_MESSAGE_TYPE,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const scrollToBottomOfConversation = () => {
  const conversation = document.getElementById('conversation')
  if (!conversation) return
  conversation.scrollTo({
    top: conversation.scrollHeight + 200,
    behavior: 'smooth',
  })
}

async function processMessages(messages, commit, state, index = 0) {
  // Base case for recursion:
  // If we've processed all messages, exit the function.
  if (index >= messages.length) return

  const message = messages[index]

  // Find the latest pending message index.
  // We need to check this before each update because the conversation state might change.
  const pendingMessageIndex = state.conversation.findLastIndex(
    (pendingMessage) => pendingMessage.type === OpenAIConstants.AI_SYSTEM_PENDING_MESSAGE_TYPE,
  )

  if (pendingMessageIndex >= 0) {
    await utils.sleep(PENDING_MESSAGE_DELAY_TIME)

    // Re-check the pending message index after the delay.
    // This is crucial because the conversation state might have changed during the delay.
    // For example, a success message might have been added, altering the pending message index.
    const pendingIndex = state.conversation.findLastIndex(
      (pendingMessage) => pendingMessage.type === OpenAIConstants.AI_SYSTEM_PENDING_MESSAGE_TYPE,
    )

    // If there's no pending message after the delay, exit the function to prevent updating the wrong message.
    if (pendingIndex < 0) return

    // Update the conversation
    const updatedConversation = [...state.conversation]
    updatedConversation[pendingMessageIndex] = {
      ...updatedConversation[pendingMessageIndex],
      content: i18nPresenter.t(message),
    }
    commit(SET_CONVERSATION, updatedConversation)
  }

  // Recursively call processMessages to process the next message.
  // We use recursion here because:
  // - We cannot use loops (for, for...of, while) with await inside them due to iterator errors or unexpected behavior.
  // - Using recursion allows us to handle asynchronous operations sequentially without such issues.
  await processMessages(messages, commit, state, index + 1)
}

const _state = {
  conversation: [],
  numberOfSlidesAdded: 0,
  messageAddedFrom: '',
}

const _getters = {
  conversation: (state) => state.conversation,
  numberOfSlidesAdded: (state) => state.numberOfSlidesAdded,
  isMessageAddedFromImportPpt: (state) => state.messageAddedFrom === OpenAIConstants.AI_DOC_TO_SLIDE_ADD_MESSAGE_FROM,
  lastUserMessageIndex(state) {
    const lastUserMessageIndex = [...state.conversation].reverse().findIndex(
      (log) => log.role === OpenAIConstants.AI_ROLE_USER,
    )
    return state.conversation.length - 1 - lastUserMessageIndex
  },
}

const actions = {
  createNewMessageAction({ commit }, messageObject) {
    try {
      commit(ADD_MESSAGE_TO_CONVERSATION, messageObject)
      scrollToBottomOfConversation()
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  resetNumberOfSlidesAddedAction({ commit }) {
    try {
      commit(SET_NUMBER_OF_SLIDES_ADDED, 0)
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  removeSystemMessageAction({ commit, state }) {
    try {
      const systemMessageIndex = state.conversation.slice().reverse().findIndex(
        (message) => message.role === OpenAIConstants.AI_ROLE_SYSTEM,
      )
      if (systemMessageIndex === -1) return

      const actualIndex = state.conversation.length - 1 - systemMessageIndex
      const updatedConversation = [...state.conversation]
      updatedConversation.splice(actualIndex, 1)
      commit(SET_CONVERSATION, updatedConversation)
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  async removeSystemMessageAndAddSuccessErrorMessageToConversationAction({ dispatch, state, rootState }, payload) {
    try {
      const { mode, numberOfSlidesAdded } = payload
      await dispatch('removeSystemMessageAction') // Remove pending message first

      const hasConversation = state.conversation && state.conversation.length > 0
      const lastMessage = hasConversation ? state.conversation[state.conversation.length - 1] : null
      if (state.numberOfSlidesAdded > 0) return // Exit if there are slides added
      if (mode === 'success') {
        await addSuccessMessageToConversation(hasConversation, lastMessage, dispatch, rootState)
      } else if (mode === 'error') {
        await addErrorMessageToConversation(hasConversation, lastMessage, dispatch, rootState)
      } else if (mode === 'generation-stopped') {
        await addGenerationStoppedMessageToConversation(hasConversation, lastMessage, dispatch, rootState)
      } else if (mode === 'slide-added') {
        await addNumberOfSlidesAddedMessageToConversation(hasConversation,
          lastMessage, numberOfSlidesAdded, dispatch, rootState)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  removeSuccessMessageAction({ commit, state }) {
    try {
      const updatedConversation = [...state.conversation]
      const lastMessageIndex = updatedConversation.length - 1
      const isLastMessageSuccess = updatedConversation[lastMessageIndex] ?
        (updatedConversation[lastMessageIndex].type === OpenAIConstants.AI_SYSTEM_SUCCESS_MESSAGE_TYPE) : false

      // Only remove if the last message is a success message
      if (isLastMessageSuccess) {
        updatedConversation.pop()
        commit(SET_CONVERSATION, updatedConversation)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  setNumberOfSlidesAddedAction: ({ commit, dispatch, state }) => {
    try {
      const updatedSlideCount = state.numberOfSlidesAdded + 1
      commit(SET_NUMBER_OF_SLIDES_ADDED, updatedSlideCount)

      const content = updatedSlideCount > 1
        ? `${updatedSlideCount} ${i18nPresenter.t('slides have been added to your presentation!')}`
        : i18nPresenter.t('1 slide has been added to your presentation!')

      // Find the index of the last message with AI_ROLE_USER
      const lastUserMessageIndex = [...state.conversation].reverse().findIndex(
        (log) => log.role === OpenAIConstants.AI_ROLE_USER,
      )
      if (lastUserMessageIndex === -1) return

      const actualUserIndex = state.conversation.length - 1 - lastUserMessageIndex
      // Find the index of the `addSlideMessage` after the last user message
      const addSlideMessageIndex = state.conversation.slice(actualUserIndex + 1).findIndex(
        (log) => log.type === OpenAIConstants.AI_SYSTEM_ADDED_SLIDE_MESSAGE_TYPE,
      )

      if (addSlideMessageIndex === -1) {
        dispatch('removeSuccessMessageAction')
        dispatch('createNewMessageAction', {
          role: OpenAIConstants.AI_ROLE_SYSTEM,
          content,
          type: OpenAIConstants.AI_SYSTEM_ADDED_SLIDE_MESSAGE_TYPE,
        })
        return
      }

      const actualAddSlideMessageIndex = actualUserIndex + 1 + addSlideMessageIndex
      state.conversation[actualAddSlideMessageIndex].content = content
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  resetConversationAction: ({ commit }) => {
    commit(SET_NUMBER_OF_SLIDES_ADDED, 0)
    commit(SET_CONVERSATION, [])
  },

  setGreetingsMessageAction({ dispatch, state, rootState }) {
    try {
      const hasGreetingsMessage = state.conversation && state.conversation.length > 0 &&
      state.conversation.some((message) => message.type === OpenAIConstants.AI_SYSTEM_GREETINGS_MESSAGE_TYPE)
      if (hasGreetingsMessage) return
      dispatch('createNewMessageAction', {
        presentationId: rootState.presentation.id,
        role: OpenAIConstants.AI_ROLE_SYSTEM,
        content: i18nPresenter.t('What do you want to create today?'),
        type: OpenAIConstants.AI_SYSTEM_GREETINGS_MESSAGE_TYPE,
      })
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  setAddMessageSourceAction({ commit }, value) {
    try {
      commit(SET_MESSAGE_ADDED_FROM, value)
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  async changePendingMessageContentOnIntervalsAction({ commit, state }) {
    try {
      const messages = OpenAIConstants.aiConversationPendingMessages.slice(1)
      await processMessages(messages, commit, state)
    } catch (error) {
      Sentry.captureException(error)
    }
  },

}

const mutations = {
  [ADD_MESSAGE_TO_CONVERSATION](state, messageObject) {
    state.conversation = [...state.conversation, messageObject]
  },
  [SET_CONVERSATION](state, newConversationArray) {
    state.conversation = newConversationArray
  },
  [SET_NUMBER_OF_SLIDES_ADDED](state, value) {
    state.numberOfSlidesAdded = value
  },
  [SET_MESSAGE_ADDED_FROM](state, value) {
    state.messageAddedFrom = value
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
